import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button";
import DatePickerInput from "~/components/formElements/DatePickerInput";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import Textarea from "~/components/formElements/Textarea";
import Headline from "~/components/Headline";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { createNonProjectInvoice } from "~/modules/billing/api/draftInvoice/draftInvoiceApiDispatchers.ts";
import InvoiceTotalsFormSection from "~/modules/billing/components/InvoiceTotalsFormSection";
import { DEFAULT_PAYMENT_TARGET_DAYS, DEFAULT_VAT_PERCENTAGE } from "~/modules/billing/constants/billingConstants.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = {
	comment: string | null;
	description: string | null;
	paymentTargetDays: number;
	recipientInformation: string | null;
	subtotalCents: number;
	vatPercentage: number;
	deliveryEndDate: Date;
	deliveryStartDate: Date;
}

type Props = {
	onCancel: () => void;
	onSuccess: () => void;
};


const CreateNonProjectInvoiceForm = ({
	onCancel,
	onSuccess,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const schema = useMemo(() => {
		return yup.object({
			comment: yup.string().nullable().default(""),
			description: yup.string().required(),
			deliveryStartDate: yup.date().required(),
			deliveryEndDate: yup
				.date()
				.required()
				.min(yup.ref("deliveryStartDate"), "Das Startdatum muss vor dem Enddatum liegen.")
				.default(null),
			paymentTargetDays: yup.number().required(),
			recipientInformation: yup.string().nullable().default(""),
			subtotalCents: yup.number().required().min(1, "Der Rechnungsbetrag darf nicht 0,00 € sein."),
			vatPercentage: yup.number().required(),
		});
	}, []);
	const defaultValues = useMemo(() => {
		return {
			comment: "",
			description: "",
			deliveryStartDate: undefined,
			deliveryEndDate: undefined,
			paymentTargetDays: DEFAULT_PAYMENT_TARGET_DAYS,
			recipientInformation: "",
			subtotalCents: 0,
			vatPercentage: DEFAULT_VAT_PERCENTAGE,
		};
	}, []);

	const {
		handleSubmit,
		control,
		formState: { isSubmitted, isValid, isDirty, errors },
		watch,
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			const invoiceData = {
				...data,
				deliveryEndDate: formatDateToYYYYMMDD(data.deliveryEndDate),
				deliveryStartDate: formatDateToYYYYMMDD(data.deliveryStartDate),
			};

			await createNonProjectInvoice(invoiceData);
			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
			setIsBusy(false);
		}
	}, [onSuccess]);


	const formIsSubmittable = useFormIsSubmittable({ isDirty, isSubmitted, isValid, isLoading: isBusy });

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Nicht projektbezogene Rechnung erstellen</SidebarHeaderHeadline>
			</SidebarHeader>
			<div className="grow gap-x-8 overflow-auto px-7 py-2">
				{isBusy && <SidebarBusyOverlay />}
				{serverErrorMessage &&
					<SidebarErrorOverlay title="Speichern fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}

				<div className="grid h-auto grid-cols-2 gap-4 pt-1">
					<div className="col-span-2">
						<Headline className="mb-2"
								  type="h4">Beschreibung</Headline>
						<Input control={control}
							   name="description"
							   placeholder="Kurze Beschreibung zum Inhalt der Rechnung"
						/>
					</div>
					<div className="col-span-2 mt-4">
						<Headline className="mb-2"
								  type="h4">Empfänger</Headline>
						<Input control={control}
							   name="recipientInformation"
							   placeholder="Name, Versandart, ggf. E-Mail-Adresse"
						/>
					</div>
					<div className="col-span-2 mt-4 grid grid-cols-2 gap-x-4">
						<Headline className="col-span-2 mb-2"
								  type="h4">Leistungszeitraum</Headline>
						<DatePickerInput name="deliveryStartDate"
										 control={control}
										 clearable={false}
										 label="Startdatum" />
						<DatePickerInput name="deliveryEndDate"
										 control={control}
										 clearable={false}
										 label="Enddatum" />
					</div>
					<div className="h-1" />
					<InvoiceTotalsFormSection<FormData> control={control}
														errorMessage={errors?.subtotalCents?.message}
														title="Rechnungsbetrag"
														watch={watch}
														includeTravelCosts={false}
					/>
					<div className="col-span-2 mt-4">
						<Headline className="mb-2"
								  type="h4">Kommentar</Headline>
						<Textarea control={control}
								  name="comment"
								  placeholder="Hier können Besonderheiten zum Abrechnungsprozess vermerkt werden."
						/>
					</div>
					<div className="col-span-2 mt-4">
						<Headline className="mb-2"
								  type="h4">Status</Headline>
						<p className="leading-tight text-gray-800">Die Rechnung wird vorerst mit dem Status &quot;In
							Arbeit&quot; erzeugt. Angaben zu Versanddatum und Zahlungsfrist können im nächsten Schritt
							getätigt werden.</p>
					</div>
				</div>
			</div>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					Rechnung erzeugen
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default CreateNonProjectInvoiceForm;
